import { isISOString } from "./is-iso-string";

/**
 * Options for configuring the `parseISOStrings` function behavior.
 *
 * @template KeepType - When true, the TypeScript return type will match the input type (though runtime transformation still occurs)
 * @template Deep - When true, recursively processes nested objects
 * @template DeepArrays - When true, recursively processes nested arrays
 */
export interface ParseISOStringsOptions<
  KeepType extends boolean = false,
  Deep extends boolean = false,
  DeepArrays extends boolean = false,
> {
  /**
   * Type-only option that affects TypeScript type inference.
   * When true, TypeScript treats the return type as unchanged from the input,
   * even though ISO strings are still converted to Date objects at runtime.
   *
   * @default false
   */
  keepType?: KeepType;

  /**
   * When true, recursively processes nested objects,
   * converting ISO strings at any depth within object properties.
   * When false, only processes top-level object properties.
   *
   * @default false
   */
  deep?: Deep;

  /**
   * When true, recursively processes nested arrays,
   * converting ISO strings at any depth within array items.
   * When false, only processes top-level array items.
   *
   * @default false
   */
  deepArrays?: DeepArrays;

  /**
   * Maximum recursion depth to prevent stack overflow from deeply nested
   * or circular structures. When the depth limit is reached, an error is thrown.
   *
   * @default 50
   */
  maxDepth?: number;

  /**
   * Optional predicate function to control which values get parsed.
   * Called with the value being considered for parsing, plus the key if
   * the value is a property of an object.
   *
   * Return `false` to skip parsing for that value (it will be kept as-is).
   * Return `true` (or any truthy value) to allow normal parsing.
   *
   * @param value - The value being considered for parsing
   * @param key - The object key, if the value is a property of an object (undefined for standalone values and array items)
   * @param parent - The parent object, if the value is a property of an object (undefined for standalone values and array items)
   */
  predicate?: <P extends Record<string, unknown>>(value: unknown, key?: string, parent?: P) => boolean;
}

/**
 * Result type for `parseISOStrings` that reflects the transformation of ISO strings to Date objects.
 *
 * @template T - The input type being transformed
 * @template KeepType - When true, returns the original type T (type-only, doesn't affect runtime)
 * @template Deep - When true, recursively transforms nested objects
 * @template DeepArrays - When true, recursively transforms nested arrays
 */
export type ParseISOStringsResult<
  T,
  KeepType extends boolean = false,
  Deep extends boolean = false,
  DeepArrays extends boolean = false,
> = KeepType extends true
  ? T
  : T extends string
  ? string | Date
  : T extends Array<infer U>
  ? DeepArrays extends true
    ? Array<ParseISOStringsResult<U, KeepType, Deep, DeepArrays>>
    : Array<U extends string ? string | Date : U>
  : T extends object
  ? {
      [K in keyof T]: T[K] extends string
        ? string | Date
        : Deep extends true
        ? ParseISOStringsResult<T[K], KeepType, Deep, DeepArrays>
        : T[K];
    }
  : T;

/**
 * Recursively parses ISO 8601 date strings and converts them to Date objects.
 *
 * This function traverses through strings, objects, and arrays, identifying valid
 * ISO 8601 timestamp strings and converting them to JavaScript Date objects.
 * It supports both shallow and deep conversion based on the provided options,
 * with separate controls for objects and arrays.
 *
 * @template T - The type of the input value
 * @template KeepType - Whether to preserve the original TypeScript type (type-only option)
 * @template Deep - Whether to recursively process nested objects
 * @template DeepArrays - Whether to recursively process nested arrays
 *
 * @param val - The value to process (string, object, array, or primitive)
 * @param options - Configuration options for the conversion
 * @param options.keepType - Type-only option that makes TypeScript treat the return type as unchanged
 * @param options.deep - When true, recursively processes nested objects
 * @param options.deepArrays - When true, recursively processes nested arrays
 * @param options.maxDepth - Maximum recursion depth (default: 50)
 *
 * @returns The transformed value with ISO strings converted to Date objects
 *
 * @throws {Error} When maximum recursion depth is exceeded
 *
 * @example
 * ```typescript
 * // Convert a simple ISO string
 * parseISOStrings('2024-01-15T10:30:00Z')
 * // Returns: Date object
 *
 * // Convert ISO strings in an object (shallow)
 * parseISOStrings({
 *   name: 'John',
 *   created: '2024-01-15T10:30:00Z',
 *   updated: '2024-01-16T10:30:00Z'
 * })
 * // Returns: { name: 'John', created: Date, updated: Date }
 *
 * // Shallow conversion doesn't process nested objects
 * parseISOStrings({
 *   user: {
 *     created: '2024-01-15T10:30:00Z',
 *     profile: { updated: '2024-01-16T10:30:00Z' }
 *   }
 * })
 * // Returns: { user: { created: '2024-01-15T10:30:00Z', profile: { ... } } }
 * // Note: nested ISO strings remain as strings
 *
 * // Deep conversion processes all object levels
 * parseISOStrings({
 *   user: {
 *     created: '2024-01-15T10:30:00Z',
 *     profile: { updated: '2024-01-16T10:30:00Z' }
 *   }
 * }, { deep: true })
 * // Returns: { user: { created: Date, profile: { updated: Date } } }
 *
 * // Convert ISO strings in arrays (shallow)
 * parseISOStrings(['2024-01-15T10:30:00Z', 'not a date'])
 * // Returns: [Date, 'not a date']
 *
 * // Deep array conversion processes nested arrays
 * parseISOStrings([['2024-01-15T10:30:00Z']], { deepArrays: true })
 * // Returns: [[Date]]
 *
 * // Combine deep options for objects and arrays
 * parseISOStrings({
 *   items: [{ created: '2024-01-15T10:30:00Z' }]
 * }, { deep: true, deepArrays: true })
 * // Returns: { items: [{ created: Date }] }
 *
 * // keepType option preserves TypeScript type (runtime still converts)
 * const result = parseISOStrings('2024-01-15T10:30:00Z' as Date, { keepType: true })
 * // TypeScript type: Date
 * ```
 */
export const parseISOStrings = <
  T,
  KeepType extends boolean = false,
  Deep extends boolean = false,
  DeepArrays extends boolean = false,
>(
  val: T,
  options?: ParseISOStringsOptions<KeepType, Deep, DeepArrays>
): ParseISOStringsResult<T, KeepType, Deep, DeepArrays> =>
  parseISOStringsInternal<T, KeepType, Deep, DeepArrays>(val, options, 0);

/**
 * Internal recursive implementation of parseISOStrings with depth tracking.
 * Do not call directly - use the public `parseISOStrings` function instead.
 *
 * @param currentDepth - Current recursion depth (managed internally)
 */
const parseISOStringsInternal = <
  T,
  KeepType extends boolean = false,
  Deep extends boolean = false,
  DeepArrays extends boolean = false,
>(
  val: T,
  options?: ParseISOStringsOptions<KeepType, Deep, DeepArrays>,
  currentDepth = 0
): ParseISOStringsResult<T, KeepType, Deep, DeepArrays> => {
  const deep = options?.deep ?? (false as Deep);
  const deepArrays = options?.deepArrays ?? (false as DeepArrays);
  const maxDepth = options?.maxDepth ?? 50;
  // Safeguard: Check recursion depth limit
  if (currentDepth >= maxDepth) {
    throw new Error(
      `Maximum recursion depth of ${maxDepth} exceeded in parseISOStrings. ` +
        `This may indicate circular references or deeply nested data structures.`
    );
  }

  const predicate = options?.predicate;

  // Handle string values
  if (typeof val === "string") {
    if (isISOString(val)) {
      if (predicate && !predicate(val)) {
        return val as ParseISOStringsResult<T, KeepType, Deep, DeepArrays>;
      }
      return new Date(val) as ParseISOStringsResult<T, KeepType, Deep, DeepArrays>;
    }
    return val as ParseISOStringsResult<T, KeepType, Deep, DeepArrays>;
  }

  // Handle null or undefined
  if (val === null || val === undefined) {
    return val as ParseISOStringsResult<T, KeepType, Deep, DeepArrays>;
  }

  // Handle arrays
  if (Array.isArray(val)) {
    return val.map((item) => {
      if (typeof item === "string" && isISOString(item)) {
        if (predicate && !predicate(item)) {
          return item;
        }
        return new Date(item);
      }
      if (deepArrays && (typeof item === "object" || Array.isArray(item))) {
        return parseISOStringsInternal(item, options, currentDepth + 1);
      }
      return item;
    }) as ParseISOStringsResult<T, KeepType, Deep, DeepArrays>;
  }

  // Handle Date objects - pass them through unchanged
  if (val instanceof Date) {
    return val as ParseISOStringsResult<T, KeepType, Deep, DeepArrays>;
  }

  // Handle objects
  if (typeof val === "object" && val !== null) {
    const result: Record<string, unknown> = {};
    // Use for...in loop to avoid creating entry tuples
    for (const key in val) {
      const value = val[key];
      if (predicate && !predicate(value, key, val as Record<string, unknown>)) {
        result[key] = value;
      } else if (typeof value === "string" && isISOString(value)) {
        result[key] = new Date(value);
      } else if (deep && typeof value === "object" && value !== null) {
        result[key] = parseISOStringsInternal(value, options, currentDepth + 1);
      } else {
        result[key] = value;
      }
    }
    return result as ParseISOStringsResult<T, KeepType, Deep, DeepArrays>;
  }

  // Return primitive values as-is
  return val as ParseISOStringsResult<T, KeepType, Deep, DeepArrays>;
};
